/**
 * RenewalRuleDrawer — Phase 9F
 *
 * Right-side slide-in drawer showing all versions for a state. Admins can:
 *   - toggle NO-pathway display mode (only when the current pathway is NO)
 *   - edit each version's fields inline
 *   - delete a version (except when it's the last remaining)
 *   - add a new dated version
 */
import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  Calendar,
  Check,
  ChevronDown,
  Info,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import Skeleton from "@/components/jea/Skeleton";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { cn } from "@/lib/utils";

const PATHWAY_YES = "YES";
const PATHWAY_NO = "NO";

const emptyVersion = {
  effective_from: "",
  renewal_pathway: PATHWAY_NO,
  regime_label: "",
  mechanism: "",
  pro_se_feasibility_note: "",
  user_facing_message: "",
  filing_instructions: "",
  trigger_type: "expiration",
  trigger_window_years: 10,
  alert_thresholds_months: "180, 90, 30, 7",
  document_type: "",
  document_display_name: "",
  renewable_indefinitely: false,
  special_flags: "",
};

const parseCsvNumbers = (str) =>
  String(str || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n));

const parseCsv = (str) =>
  String(str || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const toEditableForm = (v) => ({
  effective_from: v.effective_from || "",
  renewal_pathway: v.renewal_pathway || PATHWAY_NO,
  regime_label: v.regime_label || "",
  mechanism: v.mechanism || "",
  pro_se_feasibility_note: v.pro_se_feasibility_note || "",
  user_facing_message: v.user_facing_message || "",
  filing_instructions: v.filing_instructions || "",
  trigger_type: v.trigger_type || "expiration",
  trigger_window_years: v.trigger_window_years || 10,
  alert_thresholds_months: (v.alert_thresholds_months || []).join(", "),
  document_type: v.document_type || "",
  document_display_name: v.document_display_name || "",
  renewable_indefinitely: !!v.renewable_indefinitely,
  special_flags: (v.special_flags || []).join(", "),
});

const buildPayload = (form) => ({
  effective_from: form.effective_from || null,
  renewal_pathway: form.renewal_pathway,
  regime_label: form.regime_label || null,
  mechanism: form.mechanism || null,
  pro_se_feasibility_note: form.pro_se_feasibility_note || null,
  user_facing_message: form.user_facing_message || null,
  filing_instructions: form.filing_instructions || null,
  trigger_type: form.trigger_type,
  trigger_window_years: Number(form.trigger_window_years) || 0,
  alert_thresholds_months: parseCsvNumbers(form.alert_thresholds_months),
  document_type: form.document_type || null,
  document_display_name: form.document_display_name || null,
  renewable_indefinitely: !!form.renewable_indefinitely,
  special_flags: parseCsv(form.special_flags),
});

// ---------------------------------------------------------------------------
// Field primitives
// ---------------------------------------------------------------------------
const Label = ({ children }) => (
  <label className="jea-caption text-text-secondary block mb-1">{children}</label>
);

const Input = React.forwardRef(({ className, ...props }, ref) => (
  <input
    ref={ref}
    {...props}
    className={cn(
      "w-full h-9 px-2 rounded-[6px] bg-bg-tertiary border border-border-default text-white text-[13px] focus:outline-none focus:border-accent-pink focus:shadow-pink-glow",
      className,
    )}
  />
));

const Select = ({ children, ...props }) => (
  <select
    {...props}
    className="w-full h-9 px-2 rounded-[6px] bg-bg-tertiary border border-border-default text-white text-[13px] focus:outline-none focus:border-accent-pink"
  >{children}</select>
);

const Textarea = (props) => (
  <textarea
    {...props}
    className="w-full min-h-[64px] px-2 py-1.5 rounded-[6px] bg-bg-tertiary border border-border-default text-white text-[13px] focus:outline-none focus:border-accent-pink"
  />
);

// ---------------------------------------------------------------------------
// Version editor form
// ---------------------------------------------------------------------------
const VersionForm = ({ initial, onCancel, onSave, isNew, saving, testidPrefix }) => {
  const [form, setForm] = useState(() => initial ? toEditableForm(initial) : { ...emptyVersion });
  useEffect(() => { if (initial) setForm(toEditableForm(initial)); }, [initial]);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isYes = form.renewal_pathway === PATHWAY_YES;

  const handleSave = () => {
    if (isYes && (!form.trigger_window_years || Number(form.trigger_window_years) < 1)) {
      toast.error("YES pathway requires trigger_window_years ≥ 1");
      return;
    }
    if (isYes && !form.document_type) {
      toast.error("YES pathway requires document_type");
      return;
    }
    onSave(buildPayload(form));
  };

  return (
    <div className="space-y-3 p-4 rounded-[8px] bg-bg-tertiary border border-accent-pink/30" data-testid={`${testidPrefix}-form`}>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Effective From (blank = original/always)</Label>
          <Input
            type="date"
            value={form.effective_from}
            onChange={(e) => patch("effective_from", e.target.value)}
            data-testid={`${testidPrefix}-effective-from`}
          />
        </div>
        <div>
          <Label>Renewal Pathway</Label>
          <Select value={form.renewal_pathway} onChange={(e) => patch("renewal_pathway", e.target.value)} data-testid={`${testidPrefix}-pathway`}>
            <option value={PATHWAY_YES}>YES — pro-se renewable</option>
            <option value={PATHWAY_NO}>NO — attorney required</option>
          </Select>
        </div>
      </div>

      <div>
        <Label>Mechanism</Label>
        <Input
          value={form.mechanism}
          onChange={(e) => patch("mechanism", e.target.value)}
          placeholder="e.g. Application for Renewal of Judgment"
          data-testid={`${testidPrefix}-mechanism`}
        />
      </div>

      <div>
        <Label>Pro-Se Feasibility Note</Label>
        <Input
          value={form.pro_se_feasibility_note}
          onChange={(e) => patch("pro_se_feasibility_note", e.target.value)}
          placeholder="Short plain-English note shown to admins."
          data-testid={`${testidPrefix}-pro-se-note`}
        />
      </div>

      {isYes && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Trigger Type</Label>
              <Select value={form.trigger_type} onChange={(e) => patch("trigger_type", e.target.value)} data-testid={`${testidPrefix}-trigger-type`}>
                <option value="expiration">expiration</option>
                <option value="dormancy">dormancy</option>
              </Select>
            </div>
            <div>
              <Label>Window (yrs)</Label>
              <Input
                type="number" min="1" max="100"
                value={form.trigger_window_years}
                onChange={(e) => patch("trigger_window_years", e.target.value)}
                data-testid={`${testidPrefix}-window-years`}
              />
            </div>
            <div>
              <Label>Regime Label (optional)</Label>
              <Input
                value={form.regime_label}
                onChange={(e) => patch("regime_label", e.target.value)}
                placeholder="e.g. Post-2021"
                data-testid={`${testidPrefix}-regime-label`}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Document Type Key</Label>
              <Input
                value={form.document_type}
                onChange={(e) => patch("document_type", e.target.value)}
                placeholder="e.g. application_for_renewal_az"
                data-testid={`${testidPrefix}-doc-type`}
              />
            </div>
            <div>
              <Label>Document Display Name</Label>
              <Input
                value={form.document_display_name}
                onChange={(e) => patch("document_display_name", e.target.value)}
                placeholder="e.g. Application for Renewal of Judgment"
                data-testid={`${testidPrefix}-doc-name`}
              />
            </div>
          </div>
          <div>
            <Label>Alert Thresholds — months before deadline (comma-separated)</Label>
            <Input
              value={form.alert_thresholds_months}
              onChange={(e) => patch("alert_thresholds_months", e.target.value)}
              placeholder="180, 90, 30, 7"
              data-testid={`${testidPrefix}-alert-thresholds`}
            />
          </div>
        </>
      )}

      <div>
        <Label>User-Facing Message</Label>
        <Textarea
          value={form.user_facing_message}
          onChange={(e) => patch("user_facing_message", e.target.value)}
          placeholder="Message shown to end users on the Renewal tab / preview modal."
          data-testid={`${testidPrefix}-user-message`}
        />
      </div>
      <div>
        <Label>Filing Instructions</Label>
        <Textarea
          value={form.filing_instructions}
          onChange={(e) => patch("filing_instructions", e.target.value)}
          placeholder="Longer filing instructions shown in the pathway card expander."
          data-testid={`${testidPrefix}-filing-instructions`}
        />
      </div>
      <div>
        <Label>Special Flags (comma-separated)</Label>
        <Input
          value={form.special_flags}
          onChange={(e) => patch("special_flags", e.target.value)}
          placeholder="e.g. AZ_pre_2013_expired_permanent"
          data-testid={`${testidPrefix}-special-flags`}
        />
      </div>

      <div className="flex items-center gap-2 pt-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 h-9 px-3 rounded-[8px] bg-accent-pink text-[#091424] hover:bg-accent-pink-light font-bold text-[13px] disabled:opacity-50"
          data-testid={`${testidPrefix}-save`}
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
          {isNew ? "Add Version" : "Save Changes"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-2 h-9 px-3 rounded-[8px] border border-border-default text-text-secondary hover:text-white text-[13px]"
          data-testid={`${testidPrefix}-cancel`}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Version card (read view + edit mode)
// ---------------------------------------------------------------------------
const VersionCard = ({ version, stateCode, canDelete, onChanged }) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const save = async (payload) => {
    setSaving(true);
    try {
      await apiClient.patch(`/admin/renewal-rules/${stateCode}/versions/${version.version_id}`, payload);
      toast.success("Version updated.");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setSaving(true);
    try {
      await apiClient.delete(`/admin/renewal-rules/${stateCode}/versions/${version.version_id}`);
      toast.success("Version deleted.");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
      setConfirmingDelete(false);
    }
  };

  const isYes = version.renewal_pathway === PATHWAY_YES;
  const label = version.effective_from || "Original / Always";

  return (
    <div
      className="rounded-[10px] border border-border-default bg-bg-secondary p-4"
      data-testid={`renewal-version-card-${version.version_id}`}
    >
      <div className="flex items-start gap-3">
        <div className={cn(
          "w-9 h-9 rounded-[8px] flex items-center justify-center shrink-0 border",
          isYes ? "bg-success/10 border-success/40 text-success" : "bg-text-muted/10 border-border-default text-text-secondary",
        )}>
          <Calendar size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="jea-mono text-white font-semibold text-[13px]">{label}</span>
            <span className={cn(
              "inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold tracking-[0.06em] uppercase border",
              isYes ? "bg-success/15 text-success border-success/40" : "bg-text-muted/15 text-text-secondary border-border-default"
            )}>
              {version.renewal_pathway}
            </span>
            {version.regime_label && (
              <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold tracking-[0.06em] uppercase border border-accent-pink/30 bg-accent-pink/10 text-accent-pink">
                {version.regime_label}
              </span>
            )}
          </div>
          <div className="text-white text-[13px] font-semibold mt-1">{version.mechanism || "—"}</div>
          <div className="text-text-secondary text-[12px] mt-1">{version.pro_se_feasibility_note || "—"}</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="w-8 h-8 rounded-[6px] border border-border-default text-text-secondary hover:text-white inline-flex items-center justify-center"
            title={expanded ? "Collapse" : "Expand"}
            data-testid={`renewal-version-toggle-${version.version_id}`}
          >
            <ChevronDown size={14} className={cn("transition-transform", expanded && "rotate-180")} />
          </button>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="w-8 h-8 rounded-[6px] border border-border-default text-text-secondary hover:text-accent-pink hover:border-accent-pink/60 inline-flex items-center justify-center"
            title="Edit version"
            data-testid={`renewal-version-edit-${version.version_id}`}
          >
            <Pencil size={12} />
          </button>
          <button
            type="button"
            onClick={() => setConfirmingDelete(true)}
            disabled={!canDelete}
            className="w-8 h-8 rounded-[6px] border border-border-default text-danger hover:border-danger/60 inline-flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
            title={canDelete ? "Delete version" : "Cannot delete the last remaining version"}
            data-testid={`renewal-version-delete-${version.version_id}`}
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      {expanded && !editing && (
        <div className="mt-3 grid grid-cols-2 gap-3 text-[12px] pt-3 border-t border-border-default" data-testid={`renewal-version-details-${version.version_id}`}>
          <div><span className="text-text-secondary">Trigger:</span> <span className="text-white">{version.trigger_type || "—"}</span></div>
          <div><span className="text-text-secondary">Window:</span> <span className="text-white">{version.trigger_window_years ? `${version.trigger_window_years} years` : "—"}</span></div>
          <div><span className="text-text-secondary">Document:</span> <span className="text-white">{version.document_display_name || version.document_type || "—"}</span></div>
          <div>
            <span className="text-text-secondary">Alert months:</span>{" "}
            <span className="text-white jea-mono">
              {(version.alert_thresholds_months || []).join(", ") || "—"}
            </span>
          </div>
          {version.special_flags?.length > 0 && (
            <div className="col-span-2">
              <span className="text-text-secondary">Flags:</span>{" "}
              <span className="text-white jea-mono">{version.special_flags.join(", ")}</span>
            </div>
          )}
          {version.user_facing_message && (
            <div className="col-span-2">
              <div className="text-text-secondary uppercase text-[10px] tracking-[0.06em] font-semibold mb-1">User message</div>
              <p className="text-white leading-5">{version.user_facing_message}</p>
            </div>
          )}
          {version.filing_instructions && (
            <div className="col-span-2">
              <div className="text-text-secondary uppercase text-[10px] tracking-[0.06em] font-semibold mb-1">Filing instructions</div>
              <p className="text-white whitespace-pre-line leading-5">{version.filing_instructions}</p>
            </div>
          )}
          <div className="col-span-2 text-text-muted text-[11px] pt-2 border-t border-border-default">
            {version.updated_by
              ? `Updated by ${version.updated_by} on ${new Date(version.updated_at).toLocaleString()}`
              : `Created by ${version.created_by || "system"} on ${new Date(version.created_at).toLocaleString()}`}
          </div>
        </div>
      )}

      {editing && (
        <div className="mt-3">
          <VersionForm
            initial={version}
            saving={saving}
            testidPrefix={`renewal-version-form-${version.version_id}`}
            onCancel={() => setEditing(false)}
            onSave={save}
          />
        </div>
      )}

      {confirmingDelete && (
        <div className="mt-3 flex items-center gap-2 p-3 rounded-[8px] border border-danger/50 bg-danger/10 text-[12px] text-white">
          <AlertTriangle size={14} className="text-danger" />
          Delete this version?
          <button onClick={doDelete} disabled={saving} className="ml-auto inline-flex items-center gap-1 h-8 px-2 rounded-[6px] bg-danger text-white font-bold" data-testid={`renewal-version-delete-confirm-${version.version_id}`}>
            {saving ? <Loader2 size={12} className="animate-spin" /> : "Yes, delete"}
          </button>
          <button onClick={() => setConfirmingDelete(false)} className="h-8 px-2 rounded-[6px] border border-border-default text-text-secondary">
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Drawer
// ---------------------------------------------------------------------------
export default function RenewalRuleDrawer({ stateCode, onClose, onChanged }) {
  const [rule, setRule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingVersion, setAddingVersion] = useState(false);
  const [savingNew, setSavingNew] = useState(false);
  const [savingMode, setSavingMode] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/admin/renewal-rules/${stateCode}`);
      setRule(data.rule);
    } catch (e) {
      toast.error(formatApiError(e));
      onClose();
    } finally {
      setLoading(false);
    }
  }, [stateCode, onClose]);

  useEffect(() => { load(); }, [load]);

  const changeDisplayMode = async (mode) => {
    setSavingMode(true);
    try {
      await apiClient.patch(`/admin/renewal-rules/${stateCode}`, { no_pathway_display_mode: mode });
      toast.success("Display mode updated.");
      await load();
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingMode(false);
    }
  };

  const submitNew = async (payload) => {
    setSavingNew(true);
    try {
      await apiClient.post(`/admin/renewal-rules/${stateCode}/versions`, payload);
      toast.success("Version added.");
      setAddingVersion(false);
      await load();
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingNew(false);
    }
  };

  const currentPathway = rule?.versions?.[0]?.renewal_pathway; // sorted null-last, so first = latest by effective_from
  const isNo = currentPathway === PATHWAY_NO;

  return (
    <>
      <motion.div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
      />
      <motion.aside
        className="fixed right-0 top-0 bottom-0 w-[640px] max-w-full bg-bg-secondary border-l border-border-default z-50 flex flex-col shadow-elevated"
        initial={{ x: 640 }} animate={{ x: 0 }} exit={{ x: 640 }}
        transition={{ type: "tween", duration: 0.25 }}
        data-testid="renewal-rule-drawer"
      >
        <header className="flex items-center gap-3 px-5 py-4 border-b border-border-default">
          <div className="min-w-0 flex-1">
            <div className="text-white font-bold text-[16px] truncate">
              {rule?.state_name || stateCode}
            </div>
            <div className="jea-mono text-[12px] text-text-secondary">{stateCode}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-[8px] border border-border-default text-text-secondary hover:text-white inline-flex items-center justify-center"
            data-testid="renewal-rule-drawer-close"
          >
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading || !rule ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
            </div>
          ) : (
            <>
              <section className="rounded-[8px] border border-border-default bg-bg-tertiary p-4" data-testid="renewal-rule-drawer-state-panel">
                <div className="flex items-center gap-2 mb-2">
                  <Info size={14} className="text-text-secondary" />
                  <span className="jea-caption text-text-secondary">NO-Pathway Display Mode</span>
                </div>
                {isNo ? (
                  <div className="flex items-center gap-3">
                    <select
                      value={rule.no_pathway_display_mode}
                      disabled={savingMode}
                      onChange={(e) => changeDisplayMode(e.target.value)}
                      className="flex-1 h-9 px-2 rounded-[6px] bg-bg-secondary border border-border-default text-white text-[13px] focus:outline-none focus:border-accent-pink"
                      data-testid="renewal-rule-drawer-display-mode"
                    >
                      <option value="attorney_required">Attorney Required (amber warning)</option>
                      <option value="no_pathway">No Renewal Pathway (neutral panel)</option>
                    </select>
                    {savingMode && <Loader2 size={14} className="animate-spin text-accent-pink" />}
                  </div>
                ) : (
                  <div className="text-text-muted text-[12px] italic">
                    N/A — current pathway is YES. This setting only affects NO-pathway states.
                  </div>
                )}
              </section>

              <section>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-bold text-[14px]">Rule Versions</h4>
                  <button
                    type="button"
                    onClick={() => setAddingVersion(true)}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-[8px] bg-accent-pink text-[#091424] hover:bg-accent-pink-light text-[12px] font-bold"
                    data-testid="renewal-rule-drawer-add-version"
                  >
                    <Plus size={12} /> Add New Rule Version
                  </button>
                </div>

                {addingVersion && (
                  <div className="mb-4">
                    <VersionForm
                      initial={null}
                      isNew
                      saving={savingNew}
                      testidPrefix="renewal-rule-drawer-new-version"
                      onCancel={() => setAddingVersion(false)}
                      onSave={submitNew}
                    />
                  </div>
                )}

                <div className="space-y-3" data-testid="renewal-rule-drawer-version-list">
                  {rule.versions.map((v) => (
                    <VersionCard
                      key={v.version_id}
                      version={v}
                      stateCode={stateCode}
                      canDelete={rule.versions.length > 1}
                      onChanged={async () => { await load(); onChanged(); }}
                    />
                  ))}
                </div>
              </section>
            </>
          )}
        </div>
      </motion.aside>
    </>
  );
}
